// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useEffect, useMemo, useState } from 'react';
import { useGlobalSearchParams, usePathname, useRouter } from 'expo-router';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getCurrentTourId, subscribeCurrentTour } from '@/services/currentTour';
import { getActiveClaimSessionSync, loadClaimSession, subscribeClaimSession, type ActiveClaimSession } from '@/services/claimSession';
import { useRoundState } from '@/stores/roundState';
import { useInviteViewer } from '@/hooks/useInviteViewer';
import { withViewerParams } from '@/services/tourInviteLink';
import appColors from '@/theme/colors';

const BAR_HEIGHT = 62;

export function BottomMenu() {
  // Android 15+ forces edge-to-edge for apps targeting SDK 35+, so this bar is drawn
  // underneath the system navigation bar. Without the inset the gesture pill covers
  // the labels — on taller phones the whole bar disappears behind it.
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const pathname = usePathname();
  const safePathname = pathname ?? '';
  const { tourId: tourParam, debug: debugParam } = useGlobalSearchParams<{ tourId?: string; debug?: string }>();
  const roundId = useRoundState((state) => state.roundId);
  const holeNumber = useRoundState((state) => state.hole);
  const roundStatus = useRoundState((state) => state.roundStatus);
  const scorerOnlyMode = useRoundState((state) => state.scorerOnlyMode);
  const requestHoleMenuOpen = useRoundState((state) => state.requestHoleMenuOpen);
  const requestHoleViewOpen = useRoundState((state) => state.requestHoleViewOpen);
  const [claimSession, setClaimSession] = useState<ActiveClaimSession | null>(() => getActiveClaimSessionSync());
  // Reactive mirror of the globally-remembered tour. Reading getCurrentTourId()
  // inside a memo is not reactive, so when HoleScreen resolves the round's real
  // tour via setCurrentTourId the menu would otherwise keep a stale value.
  const [storedTourId, setStoredTourId] = useState<string | null>(() => getCurrentTourId());
  const { user } = useUser();
  // A spectator's whole state lives in the URL, so every one of these jumps has to carry it —
  // drop it and an anonymous viewer is bounced to Keycloak, while a signed-in one loses the
  // invite credential and gets "Unable to load rounds" on a tour they are not a member of.
  const { viewerRouteParams } = useInviteViewer();
  // withViewerParams returns a plain string and expo-router's typed routes only accept literals.
  // These paths are built from a valid route plus query params, so the cast is safe — the same
  // escape hatch HoleScreen's router shim uses for its dynamically-built routes.
  const pushViewerAware = (path: string) => router.push(path as never);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { t } = useI18n();
  const [singleRoundTourIds, setSingleRoundTourIds] = useState<Record<string, boolean>>({});
  // The round's own tour id, resolved from the round payload. This is authoritative
  // for the active round and beats a stale localStorage-persisted current tour (which
  // can point at a previous hidden single-round tour and wrongly disable the button).
  const [roundTourId, setRoundTourId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setStoredTourId(getCurrentTourId());
    return subscribeCurrentTour((nextTourId) => {
      setStoredTourId(nextTourId);
    });
  }, []);

  const roundIdFromPath = useMemo(() => {
    const parts = safePathname.split('/').filter(Boolean);
    const idx = parts.indexOf('round');
    if (idx < 0) return undefined;
    return parts[idx + 1];
  }, [safePathname]);
  const holeFromPath = useMemo(() => {
    const parts = safePathname.split('/').filter(Boolean);
    const idx = parts.indexOf('hole');
    if (idx < 0) return null;
    const raw = parts[idx + 1];
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [safePathname]);
  // The round id in the current URL is authoritative: when viewing a round-scoped
  // screen (e.g. another round's summary), the menu must target that round, not a
  // previously remembered one.
  const activeRoundId = roundIdFromPath ?? roundId ?? claimSession?.startedRoundId ?? undefined;
  // The stored hole belongs to the stored round; ignore it when the URL points at
  // a different round.
  const storedHoleForActiveRound = !roundIdFromPath || roundIdFromPath === roundId ? holeNumber : null;
  const activeHoleNumber = holeFromPath ?? storedHoleForActiveRound ?? (activeRoundId ? 1 : null);
  const hasRoundContext = !!activeRoundId;
  const hasRoundHoleContext = hasRoundContext && !!activeHoleNumber;
  const isFinishedRound = String(roundStatus ?? '').trim().toLowerCase() === 'finished';
  const hasClaimNavigationAccess = !!claimSession?.claimAccessToken;
  // A "single round" is stored as a hidden tour and has no tour-level views, so the
  // Tour button is disabled for it.
  const currentTourId = useMemo(
    () => tourParam ?? roundTourId ?? storedTourId ?? claimSession?.tourId ?? null,
    [tourParam, roundTourId, storedTourId, claimSession?.tourId]
  );
  // Resolve the active round's real tour so the Tour button reflects that round's
  // tour regardless of which screen opened it or what stale tour is remembered.
  useEffect(() => {
    if (!activeRoundId || !user?.token) {
      setRoundTourId(null);
      return;
    }
    let cancelled = false;
    fetch(`${apiBaseUrl}/api/rounds/${activeRoundId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        const resolved = (data.tourId as string | undefined) ?? (data.TourId as string | undefined) ?? null;
        setRoundTourId(resolved);
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [activeRoundId, apiBaseUrl, user?.token]);
  useEffect(() => {
    if (!currentTourId || !user?.token) return;
    if (currentTourId in singleRoundTourIds) return;
    let cancelled = false;
    fetch(`${apiBaseUrl}/api/tours/${currentTourId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        const hidden = (data.isHidden as boolean | undefined) ?? (data.IsHidden as boolean | undefined) ?? false;
        setSingleRoundTourIds((prev) => ({ ...prev, [currentTourId]: hidden }));
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, currentTourId, singleRoundTourIds, user?.token]);
  const isSingleRoundTour = !!currentTourId && singleRoundTourIds[currentTourId] === true;
  const disableTourNavigation = (scorerOnlyMode && !hasClaimNavigationAccess) || isSingleRoundTour;
  const disableRoundNavigation = (!hasRoundContext) || (scorerOnlyMode && !hasClaimNavigationAccess);
  const isHoleRoute = /\/round\/[^/]+\/hole\//.test(safePathname);
  // The aerial hole view is open to every signed-in player. It needs the user's token to
  // fetch the hole geometry, so a signed-out bar keeps its four items. Courses without
  // derived geometry are handled by the view itself, which says so instead of drawing.
  // The view belongs to the hole screen, hence the request-version handoff through the
  // round store — the same route the hole menu already takes.
  const canUseHoleView = !!user?.token;
  const disableHoleViewNavigation = !isHoleRoute || !hasRoundHoleContext;

  const handleTourPress = () => {
    if (disableTourNavigation) return;
    if (currentTourId) {
      pushViewerAware(withViewerParams(`/tour/${currentTourId}/leaderboard`, viewerRouteParams));
    } else {
      router.push('/my-tours');
    }
  };

  const handleHolePress = () => {
    if (!hasRoundContext) {
      return;
    }
    if (isHoleRoute) {
      requestHoleMenuOpen();
      return;
    }
    const params: string[] = [];
    if (debugParam === 'true') params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    if (activeRoundId && activeHoleNumber) {
      const nextHole = activeHoleNumber;
      pushViewerAware(withViewerParams(`/round/${activeRoundId}/hole/${nextHole}/compact${query}`, viewerRouteParams));
      return;
    }
  };

  const handleHoleViewPress = () => {
    if (disableHoleViewNavigation) return;
    requestHoleViewOpen();
  };

  const handleScorecardPress = () => {
    if (activeRoundId && hasRoundContext) {
      pushViewerAware(withViewerParams(`/round/${activeRoundId}/scorecard`, viewerRouteParams));
      return;
    }
  };

  const handleRoundResultsPress = () => {
    if (disableRoundNavigation) return;
    if (activeRoundId && hasRoundContext) {
      const parts: string[] = [];
      if (tourParam) parts.push(`tourId=${tourParam}`);
      if (debugParam === 'true') parts.push('debug=true');
      const query = parts.length ? `?${parts.join('&')}` : '';
      pushViewerAware(withViewerParams(`/round/${activeRoundId}/summary${query}`, viewerRouteParams));
      return;
    }
  };

  const isTourActive = safePathname.startsWith('/tour') || safePathname.startsWith('/my-tours');
  const isHoleActive = safePathname.includes('/hole/');
  const isRoundActive = safePathname.includes('/summary') || safePathname.includes('/select-games') || safePathname.includes('/roundstart');
  const isScorecardActive = safePathname.includes('/scorecard');

  return (
    <View style={[styles.container, { height: BAR_HEIGHT + insets.bottom, paddingBottom: insets.bottom }]}>
      <Pressable
        style={[styles.item, isHoleActive && styles.itemActive, (!hasRoundContext || isFinishedRound) && styles.itemDisabled]}
        onPress={handleHolePress}
        disabled={!hasRoundContext || isFinishedRound}
      >
        <FontAwesome name="flag-checkered" size={18} color={hasRoundContext && !isFinishedRound ? (isHoleActive ? appColors.accent.primary : appColors.text.secondary) : appColors.text.muted} />
        <Text style={[styles.label, isHoleActive && styles.labelActive, (!hasRoundContext || isFinishedRound) && styles.labelDisabled]}>{t('menu.hole')}</Text>
      </Pressable>
      <Pressable
        testID="bottom-menu-tour"
        style={[styles.item, isTourActive && !disableTourNavigation && styles.itemActive, disableTourNavigation && styles.itemDisabled]}
        onPress={handleTourPress}
        disabled={disableTourNavigation}
      >
        <FontAwesome
          name="trophy"
          size={18}
          color={disableTourNavigation ? appColors.text.muted : (isTourActive ? appColors.accent.primary : appColors.text.secondary)}
        />
        <Text style={[styles.label, isTourActive && !disableTourNavigation && styles.labelActive, disableTourNavigation && styles.labelDisabled]}>
          {t('menu.tour')}
        </Text>
      </Pressable>
      <Pressable
        style={[styles.item, isRoundActive && !disableRoundNavigation && styles.itemActive, disableRoundNavigation && styles.itemDisabled]}
        onPress={handleRoundResultsPress}
        disabled={disableRoundNavigation}
      >
        <FontAwesome
          name="list-alt"
          size={18}
          color={hasRoundContext && !disableRoundNavigation
            ? (isRoundActive ? appColors.accent.primary : appColors.text.secondary)
            : appColors.text.muted}
        />
        <Text style={[styles.label, isRoundActive && !disableRoundNavigation && styles.labelActive, disableRoundNavigation && styles.labelDisabled]}>
          {t('menu.roundResults')}
        </Text>
      </Pressable>
      <Pressable
        style={[styles.item, isScorecardActive && styles.itemActive, !hasRoundContext && styles.itemDisabled]}
        onPress={handleScorecardPress}
        disabled={!hasRoundContext}
      >
        <FontAwesome name="th" size={18} color={hasRoundContext ? (isScorecardActive ? appColors.accent.primary : appColors.text.secondary) : appColors.text.muted} />
        <Text style={[styles.label, isScorecardActive && styles.labelActive, !hasRoundContext && styles.labelDisabled]}>{t('menu.scorecard')}</Text>
      </Pressable>
      {canUseHoleView ? (
        <Pressable
          testID="bottom-menu-hole-view"
          style={[styles.item, disableHoleViewNavigation && styles.itemDisabled]}
          onPress={handleHoleViewPress}
          disabled={disableHoleViewNavigation}
        >
          <FontAwesome
            name="map-o"
            size={18}
            color={disableHoleViewNavigation ? appColors.text.muted : appColors.text.secondary}
          />
          <Text style={[styles.label, disableHoleViewNavigation && styles.labelDisabled]}>
            {t('menu.holeView')}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: BAR_HEIGHT,
    backgroundColor: 'rgba(1, 24, 6, 0.8)',
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 10,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      },
    }),
  },
  item: {
    minWidth: 70,
    // A fifth item (the developer-only hole view) takes the bar past the width of a
    // narrow phone, so items give way rather than overflowing the bar.
    flexShrink: 1,
    minHeight: 42,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  itemActive: {
    backgroundColor: appColors.accent.subtleBg,
  },
  itemDisabled: {
    opacity: 0.75,
  },
  label: {
    color: appColors.text.secondary,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  labelActive: {
    color: appColors.accent.primary,
    fontWeight: '700',
  },
  labelDisabled: {
    color: appColors.text.muted,
  },
});
